/**
 * Server-side utility functions for determining chart availability
 */

import type { EnrichedFinalJob } from '../../../../../lambda/types';
import { hasAnyValidNumber } from './numberUtils';

/**
 * Type for job with all possible enrichment fields
 * The API may return additional fields beyond the base types
 */
type JobWithEnrichment = EnrichedFinalJob & {
  companyRevenue?: string | number;
  companyProfit?: string | number;
  companyProfitMargin?: string | number;
};

/**
 * Checks if any charts are available for a job
 * This should be calculated server-side to prevent CLS (Cumulative Layout Shift)
 * 
 * The logic matches the client-side calculation in +Page.tsx
 */
export function calculateHasAnyCharts(
  job: EnrichedFinalJob,
  locale: string = 'en'
): boolean {
  const enrichedJob = job as JobWithEnrichment;
  
  // Get trends for current locale, fallback to English if not available
  const companyHiringTrendsByLocale = enrichedJob.companyHiringTrendsByLocale;
  const currentLocaleTrends = companyHiringTrendsByLocale?.[locale as 'en' | 'fi' | 'sv'] 
    || companyHiringTrendsByLocale?.en 
    || enrichedJob.companyHiringTrends;

  // Check financial data
  const companyRevenue = enrichedJob.companyRevenue;
  const companyProfit = enrichedJob.companyProfit;
  const companyProfitMargin = enrichedJob.companyProfitMargin;
  const hasFinancial = hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin);

  // Check hiring data
  const companyMonthlyCounts = enrichedJob.companyMonthlyCounts;
  const hasHiring = !!(companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0);

  // Check locations data
  const hasLocations = !!(currentLocaleTrends &&
    ((currentLocaleTrends.locationDistribution &&
      Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
      Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
     (currentLocaleTrends.remotenessDistribution &&
      (currentLocaleTrends.remotenessDistribution.remote > 0 ||
       currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
       currentLocaleTrends.remotenessDistribution.onsite > 0))));

  // Check roles data
  const hasRoles = !!(currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution));

  // Check languages data
  const hasLanguages = !!(currentLocaleTrends?.languageRequirements &&
    Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
    Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val)));

  // Check map data
  const companyGeocoordinates = enrichedJob.companyGeocoordinates;
  const hasMap = !!companyGeocoordinates;

  return hasFinancial || hasHiring || hasLocations || hasRoles || hasLanguages || hasMap;
}
